import React from 'react'
import type { ButtonHTMLAttributes } from 'react'
import { Span } from './typography'

type ActionButtonVariant = 'primary' | 'secondary' | 'tertiary'
type ActionButtonSize = 'sm' | 'md' | 'lg'

interface ActionButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ActionButtonVariant
  size?: ActionButtonSize
  dot?: boolean
  icon?: React.ElementType
  number?: number
}

const BASE =
  'relative inline-flex shrink-0 cursor-pointer items-center justify-center rounded-full transition focus:outline-none'

const VARIANTS: Record<ActionButtonVariant, string> = {
  primary:
    'bg-indigo-base text-white ' +
    'hover:not-active:bg-indigo-600 hover:not-active:shadow-btn-hover ' +
    'active:bg-indigo-600 ' +
    'focus-visible:ring-2 focus-visible:ring-indigo-800 ' +
    'disabled:pointer-events-none disabled:bg-neutral-50 disabled:text-neutral-300 disabled:shadow-none',
  secondary:
    'border border-indigo-base bg-transparent text-indigo-base ' +
    'hover:not-active:bg-indigo-50 hover:not-active:shadow-btn-hover ' +
    'active:bg-indigo-100 active:border-indigo-100 ' +
    'focus-visible:ring-2 focus-visible:ring-indigo-800 focus-visible:border-transparent ' +
    'disabled:pointer-events-none disabled:border-neutral-300 disabled:bg-neutral-50 disabled:text-neutral-300',
  tertiary:
    'bg-transparent text-indigo-base ' +
    'hover:not-active:bg-indigo-50 ' +
    'active:bg-indigo-100 ' +
    'focus-visible:bg-indigo-50 focus-visible:ring-2 focus-visible:ring-indigo-800 ' +
    'disabled:pointer-events-none disabled:text-neutral-300',
}

const SIZES: Record<ActionButtonSize, string> = {
  sm: 'size-8',
  md: 'size-10',
  lg: 'size-12',
}

const ICON_SIZES: Record<ActionButtonSize, string> = {
  sm: 'h-4 w-4',
  md: 'h-4 w-4',
  lg: 'h-5 w-5',
}

const DOT_CLASSES: Record<ActionButtonSize, string> = {
  sm: 'left-3.75 bottom-3.75',
  md: 'left-5 bottom-5',
  lg: 'left-6 bottom-6.5',
}

export function ActionButton({
  variant = 'primary',
  size = 'lg',
  dot = false,
  icon: ButtonIcon,
  number,
  disabled,
  className = '',
  ...props
}: ActionButtonProps) {
  const classes = [BASE, VARIANTS[variant], SIZES[size], className]
    .filter(Boolean)
    .join(' ')

  const dotClasses = DOT_CLASSES[size]

  const content = ButtonIcon ? (
    <ButtonIcon className={ICON_SIZES[size]} />
  ) : number !== undefined ? (
    <Span className="leading-none">{number}</Span>
  ) : (
    <></>
  )

  return (
    <button type="button" className={classes} disabled={disabled} {...props}>
      {content}
      {dot && (
        <span
          className={`absolute size-2.75 rounded-full border border-white ${dotClasses} ${disabled ? 'bg-rose-300' : 'bg-coral-base'}`}
        />
      )}
    </button>
  )
}
